import qs from 'qs'
import axios, { AxiosRequestConfig } from 'axios'
import { MessageBox, Message } from 'element-ui'
import { getToken, logout } from '@ps/utils/auth'
import { v2Host } from '@/config/host'
import i18n from '@ps/lang'
import { localLanguage } from '@/common/storage'
import { LogoutType } from '@pt/enums/logs'

// create an axios instance
const service = axios.create({
  baseURL: v2Host, // url = base url + request url
  withCredentials: true, // send cookies when cross-domain requests
  timeout: 600000, // request timeout
})

export enum ResponseCode {
  Success = 0,
  Other = 1,
  Unlogin = 2,
  Authorization = 4,
  ProjectNotExist = 4004,
  AssetNotExists = 600002, // 资源不存在，可能已被删除
}

// request interceptor
service.interceptors.request.use(
  (config) => {
    const token = getToken()

    // http header 加 token
    if (token && typeof token === 'string') {
      config.headers['auth-token'] = token
    }

    const lang = localLanguage.get()
    // http header 加语言
    if (lang) {
      config.headers['Accept-Language'] = lang
    }

    if (
      config.method?.toLowerCase() === 'post' &&
      !(config.data instanceof FormData)
    ) {
      // 没有传 Content-Type，设置认为是 application/x-www-form-urlencoded 且将 config.data 转换为合法的格式
      if (!config.headers['Content-Type']) {
        config.data = qs.stringify(config.data)
        config.headers['Content-Type'] = 'application/x-www-form-urlencoded'
      }
    }

    return config
  },

  (error) => {
    // do something with request error
    console.log(error) // for debug
    return Promise.reject(error)
  },
)

function promptPermissionError(msg: string) {
  MessageBox.confirm(
    msg,
    i18n.t('common.request.permissionError.title') as string,
    {
      confirmButtonText: i18n.t('common.confirmButtonText') as string,
      cancelButtonText: i18n.t('common.cancelButtonText') as string,
      type: 'error',
    },
  ).catch()
}

const codeMessage = {
  2: i18n.t('common.request.codeMessage.message1'),
  4: i18n.t('common.request.codeMessage.message2'),
}

// response interceptor
service.interceptors.response.use(
  (response) => {
    const data = response.data

    // 0: 正常，1：其他错误，2：未登录，4：权限错误
    // 未登录或 token 失效去读取接口
    if (data.code === ResponseCode.Unlogin) {
      const errorMessage = data.msg || codeMessage[2]
      logout({
        type: LogoutType.APIError,
        msg: errorMessage,
      })
      return Promise.reject(Object.assign({}, data, { message: errorMessage }))
    }

    // 权限错误
    if (data.code === ResponseCode.Authorization) {
      const errorMessage = data.msg || codeMessage[4]
      promptPermissionError(errorMessage)
      return Promise.reject(Object.assign({}, data, { message: errorMessage }))
    }

    return data
  },
  (error) => {
    // 由于项目初始化时就统一加了错误弹窗，但是取消请求时不需要弹窗错误提示，所以只能暂时这么处理
    // TODO: 优化请求取消处理方式
    if (error.message === 'cancel request') {
      // do something
      console.log('接口请求被取消')
      return Promise.reject(error)
    }
    console.log('err' + error) // for debug
    Message({
      message: error.message,
      type: 'error',
      duration: 5 * 1000,
      showClose: true,
    })
    return Promise.reject(error)
  },
)

export default function request<T>(config: AxiosRequestConfig): T {
  return service(config) as any
}
